// Stripe plan configuration for Dibs Omnichannel CRM AI + add-ons.
//
// Two product taxonomy:
//  1. CRM plan (Free / Growing / Premium / Enterprise). The main subscription
//  2. Add-ons (e.g. Dibs Bookings). Separate subscription items on top of the CRM plan
//
// A Stripe Subscription can hold multiple line items: one for the CRM plan,
// one per add-on. Resolve each item independently via the helpers below.
//
// Supports dual-mode: test (sandbox) and live, controlled by VITE_STRIPE_MODE.

export const isStripeTestMode = import.meta.env.VITE_STRIPE_MODE === "test";

// Types

export type BillingCycle = "monthly" | "quarterly" | "yearly";

export const BILLING_CYCLES: readonly BillingCycle[] = ["monthly", "quarterly", "yearly"];

/** Months covered by one charge on each cycle. */
export const CYCLE_MONTHS: Record<BillingCycle, number> = { monthly: 1, quarterly: 3, yearly: 12 };

export const BILLING_CYCLE_LABELS: Record<BillingCycle, string> = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  yearly: "Yearly",
};

/** Short unit for "S$540/quarter"-style amounts. */
export const CYCLE_UNIT: Record<BillingCycle, string> = { monthly: "mo", quarterly: "quarter", yearly: "yr" };

export interface PlanLimits {
  outlets: number | null;
  staff: number | null;
  listings: number | null;
  /** Saved contacts the plan holds. null = unlimited. */
  contacts_storage: number | null;
  /** Base monthly MAC allowance included in the plan. */
  mac_base: number | null;
  /** Hard ceiling, even with overage, business cannot exceed this. null = unlimited. */
  mac_ceiling: number | null;
  /** MACs granted per overage pack. Overage is sold in fixed packs only. null = overage disabled (Free/Enterprise). */
  mac_overage_pack_size: number | null;
  /** USD charged per overage pack, postpaid. null = overage disabled (Free/Enterprise). */
  mac_overage_pack_price_usd: number | null;
  /** USD per broadcast message sent, postpaid. null = broadcasts disabled. */
  broadcast_rate_per_message: number | null;
}

export interface PlanFeatures {
  ai_auto_reply: boolean;
  ai_campaigns: boolean;
  synopsis_generation: boolean;
  analytics_dashboard: boolean;
  role_based_access: boolean;
  unlimited_contact_storage: boolean;
  /** Scheduled Messages ("scheduled broadcasts") feature. Premium+ only. */
  scheduled_broadcasts: boolean;
  /**
   * Whether the business may run automations. The six workflow automations
   * (auto-reply, appointment reminders, no-show recovery, booking confirmation,
   * dormant win-back, conversation follow-up). The $1 Builder plan sets this
   * false: a Builder business can import contacts and generate hyper-personalised
   * customer profiles, but no automation will fire until it upgrades. Enforced
   * server-side in supabase/functions/_shared/automations/registry.ts.
   */
  automations: boolean;
  /**
   * Building workflows from scratch, changing a template's steps, or asking
   * the AI to build one. Without it a business can still use templates as-is
   * (wording only). Enforced at publish in workflow-definitions.
   */
  custom_workflows: boolean;
  /** Custom MCP/API business-data connections (feeds, APIs, actions, MCP). */
  custom_connections: boolean;
  /** Ask Dibs AI: the Home assistant and the workflow assistant. */
  ask_dibs_ai: boolean;
}

interface StripePrice {
  priceId: string;
  productId: string;
}

export interface PlanConfig {
  key: string;
  name: string;
  description: string;
  /** Per-month price on each cycle. No quarterly price = not sold quarterly. */
  price: { monthly: number; quarterly?: number; yearly: number };
  limits: PlanLimits;
  features: PlanFeatures;
  /** Stripe IDs. Null for Free (no Stripe subscription) and Enterprise (custom-sold). */
  stripe: {
    monthly: StripePrice;
    quarterly?: StripePrice;
    yearly: StripePrice;
  } | null;
}

export interface AddOnConfig {
  key: string;
  name: string;
  description: string;
  /** Per-month price. The quarterly price charges three months at once. */
  price: { monthly: number };
  limits: Record<string, number | null>;
  /**
   * One price per cycle the add-on can join. Stripe can't mix intervals on one
   * subscription, so a plan billed yearly can't take an add-on without a yearly price.
   */
  stripe: {
    monthly: StripePrice;
    quarterly?: StripePrice;
  };
}

export interface ResolvedSubscription {
  planKey: string;
  addOnKeys: string[];
}

/**
 * Current Dibs Bookings Stripe catalog: S$20/mo and S$60/quarter (2026-10-24).
 * Monthly reuses the S$20 price that preceded the S$30 one; the quarterly
 * price sits on that price's product.
 */
export const BOOKINGS_STRIPE_CATALOG = {
  test: {
    monthly: { priceId: "price_1Ten1GFlAk9QGxZMHDdZYFsj", productId: "prod_Ue5BzOTQSOhDvb" },
    quarterly: { priceId: "price_1UTwTsFlAk9QGxZMjVmYXcnt", productId: "prod_Ue5BzOTQSOhDvb" },
  },
  live: {
    monthly: { priceId: "price_1TnJzzFlAk9QGxZMgPRcoYJg", productId: "prod_UmtnY96HDj7qzv" },
    quarterly: { priceId: "price_1UTw33FlAk9QGxZMebAFSwNG", productId: "prod_UmtnY96HDj7qzv" },
  },
} as const;

// Legacy price IDs
// Kept so grandfathered subscribers on old prices still resolve.
// The old $49 Growth and $99 Premium price IDs continue to resolve to
// "growing" and "premium" respectively. The old $19 Starter price ID is
// now the Bookings add-on (see LIVE_ADDONS / TEST_ADDONS below).
const LEGACY_PRICE_TO_PLAN: Record<string, string> = {
  // Old Growth. Test (Sandbox)
  "price_1TELtlFx39VnBqRbjXwNvq6X": "growing",
  "price_1TELxvFx39VnBqRbMfm3j2hE": "growing",
  // Old Premium. Test (Sandbox)
  "price_1TELurFx39VnBqRbQVqkOGNY": "premium",
  "price_1TELz3Fx39VnBqRbw6ru4FEu": "premium",
  // Old $39/$31 Growth. Live (grandfathered)
  "price_1TMOIXFlAk9QGxZM9Ei7rPoF": "growing",
  "price_1TMOIuFlAk9QGxZMVLRm3JLc": "growing",
  // Old $79/$63 Premium. Live (grandfathered)
  "price_1TMOJdFlAk9QGxZMQAkvjctX": "premium",
  "price_1TMOJyFlAk9QGxZMr5yuBWwz": "premium",
  // Old $49/$39 Growth. Live (previous tier, before $269/$199)
  "price_1TOd24FlAk9QGxZMaOSwEANr": "growing",
  "price_1TOd33FlAk9QGxZMbdhGJbSl": "growing",
  // Old $99/$79 Premium. Live (previous tier, before $549/$479)
  "price_1TOd3gFlAk9QGxZMhPjLmZir": "premium",
  "price_1TOd44FlAk9QGxZMnKlqKoVH": "premium",
  // Old $269/$199 Growth. Live (previous tier, before $250/$200)
  "price_1TXMMhFlAk9QGxZMEFxZyAE5": "growing",
  "price_1TXMNVFlAk9QGxZMJ6GsuzAH": "growing",
  // Old $549/$479 Premium. Live (previous tier, before $500/$400)
  "price_1TXMO5FlAk9QGxZMyIdIOuwP": "premium",
  "price_1TXMOXFlAk9QGxZMxYCyeZLC": "premium",
  // Prior Starter/Growth/Premium IDs. Live (grandfathered after the latest reprice)
  "price_1TecwAFlAk9QGxZMR8iuZAww": "starter",
  "price_1TecwqFlAk9QGxZMSW8mVMGK": "starter",
  "price_1TeczGFlAk9QGxZM5Arfa2A1": "growing",
  "price_1TeczRFlAk9QGxZMRDfmiuMo": "growing",
  "price_1TeczsFlAk9QGxZMnsinyTRB": "premium",
  "price_1Ted0DFlAk9QGxZMdM1348xe": "premium",
  // TEST Starter S$200 / Growth S$350 prices, retired 2026-09-29 when Starter
  // became Lite (S$40) and Growth moved to S$200. Both map to Growth: anyone
  // on them was paying Growth money or more. (Live repriced the same price
  // IDs in place, so live has no retired IDs from this change.)
  "price_1Tmn5rFlAk9QGxZMacGPwtKV": "growing",
  "price_1Tmn6OFlAk9QGxZMnD3EAyWN": "growing",
  "price_1Tmn6vFlAk9QGxZMkAFZJrBp": "growing",
  "price_1Tmn86FlAk9QGxZMmjXzSanL": "growing",
  // Prior Starter/Growth/Premium IDs. Test (grandfathered after the latest reprice)
  "price_1TemzFFlAk9QGxZMO6xYDyFM": "starter",
  "price_1TemzeFlAk9QGxZMqju59ZIQ": "starter",
  "price_1TemzyFlAk9QGxZM7cb4NYNG": "growing",
  "price_1Ten0GFlAk9QGxZMpLErcid8": "growing",
  "price_1Ten0hFlAk9QGxZMS7TNe0fJ": "premium",
  "price_1Ten0yFlAk9QGxZMYBciVwvL": "premium",
  // Lite S$40 / S$384 (live + test), retired 2026-10-24 when Lite became
  // Starter. Those subscribers resolve to Starter.
  "price_1TmmU1FlAk9QGxZMWk7U9mo6": "starter",
  "price_1TmmUkFlAk9QGxZMIpdlcLLr": "starter",
  "price_1UKvIlFlAk9QGxZMWabsFoA6": "starter",
  "price_1UKvImFlAk9QGxZMIN4jXrq8": "starter",
  // Growth S$200 / S$1,920 (live + test), retired 2026-10-24. Grandfathered:
  // those subscribers stay on Growth at their price.
  "price_1TmmV6FlAk9QGxZMZsxiT78w": "growing",
  "price_1TmmXnFlAk9QGxZMFW5HEW4q": "growing",
  "price_1UKvImFlAk9QGxZMcGQsF9ej": "growing",
  "price_1UKvImFlAk9QGxZMQbx4ptzM": "growing",
};

// Legacy add-on price IDs so existing subscribers continue to resolve to the
// Bookings add-on after each reprice.
const LEGACY_ADDON_PRICE_IDS: Record<string, string> = {
  // S$30 (live + test), retired 2026-10-24. Existing subscribers keep it.
  "price_1TzfEgFlAk9QGxZMrhs9k5F4": "bookings",
  "price_1TzfGpFlAk9QGxZMRW2kC1kG": "bookings",
  // Live ($19)
  "price_1TMOEuFlAk9QGxZMC4WiI5dA": "bookings",
  "price_1TMOG4FlAk9QGxZMlMJM8hiO": "bookings",
  // Live. Prior $20 add-on price/product that went missing from Stripe.
  // Kept so any subscription still attached to it resolves to the add-on.
  "price_1Ted1PFlAk9QGxZMCBEQ1BRj": "bookings",
  // Live ($20, immediately preceding the $30 catalog).
  "price_1TnJzzFlAk9QGxZMgPRcoYJg": "bookings",
  // Test ($20, immediately preceding the $30 catalog).
  "price_1Ten1GFlAk9QGxZMHDdZYFsj": "bookings",
  // Test (Sandbox)
  "price_1TELrdFx39VnBqRbG7rVKwwA": "bookings",
  "price_1TELx1Fx39VnBqRbtz1MLCYS": "bookings",
};

// Sandbox (Test) plans
// Test mode has its own Stripe price IDs at the published SGD prices
// (Sandbox S$1, Starter S$200, Growth S$450, Premium S$600), so test-mode
// checkouts charge the same amounts as live. Prior test price IDs are kept in
// LEGACY_PRICE_TO_PLAN so any existing test subscriptions still resolve.
const TEST_PLANS: Record<string, PlanConfig> = {
  // $1/mo Builder. Sign up and explore before launch. Import contacts and
  // generate hyper-personalised customer profiles, but NO automations run until
  // the business upgrades to a real plan. Self-serve on the pricing page.
  builder: {
    key: "builder",
    name: "Sandbox",
    description: "Explore Dibs before you launch — import contacts and build hyper-personalised customer profiles. Upgrade to turn on automations.",
    price: { monthly: 1, quarterly: 1, yearly: 1 },
    limits: {
      outlets: 1,
      staff: 1,
      listings: null,
      contacts_storage: null,
      mac_base: 50,
      mac_ceiling: 50,
      mac_overage_pack_size: null,
      mac_overage_pack_price_usd: null,
      broadcast_rate_per_message: null,
    },
    features: {
      ai_auto_reply: false,
      ai_campaigns: false,
      synopsis_generation: false,
      analytics_dashboard: false,
      role_based_access: false,
      unlimited_contact_storage: true,
      scheduled_broadcasts: false,
      automations: false,
      custom_workflows: false,
      custom_connections: false,
      ask_dibs_ai: false,
    },
    stripe: {
      // Sandbox ($1/mo, internal key "builder"). TEST. Monthly-only; quarterly and yearly reuse the monthly price ID.
      monthly: { priceId: "price_1TmmFlFlAk9QGxZMUeAIE8or", productId: "prod_UmKvCquBgbOS6r" },
      quarterly: { priceId: "price_1TmmFlFlAk9QGxZMUeAIE8or", productId: "prod_UmKvCquBgbOS6r" },
      yearly: { priceId: "price_1TmmFlFlAk9QGxZMUeAIE8or", productId: "prod_UmKvCquBgbOS6r" },
    },
  },
  // Minimal floor for cancelled / no-subscription / add-on-only businesses.
  // NOT a sellable tier, not rendered on the pricing page.
  free: {
    key: "free",
    name: "Free",
    description: "Minimal floor for accounts without an active paid plan",
    price: { monthly: 0, yearly: 0 },
    limits: {
      outlets: 1,
      staff: 1,
      listings: null,
      contacts_storage: null,
      mac_base: 50,
      mac_ceiling: 50,
      mac_overage_pack_size: null,
      mac_overage_pack_price_usd: null,
      broadcast_rate_per_message: null,
    },
    features: {
      ai_auto_reply: true,
      ai_campaigns: false,
      synopsis_generation: false,
      analytics_dashboard: false,
      role_based_access: false,
      unlimited_contact_storage: true,
      automations: true,
      scheduled_broadcasts: false,
      custom_workflows: false,
      custom_connections: false,
      ask_dibs_ai: false,
    },
    stripe: null,
  },
  // Starter: a single studio just starting out. One branch, owner only (no
  // staff seats). Replaced Lite (S$40) on 2026-10-24; the key stayed "starter",
  // so Lite subscribers resolve here. Unlike Growth: templates as-is only (no
  // custom workflows), no custom MCP/API, no Ask Dibs AI, and no AI-inferred
  // profile attributes (given + calculated facts only).
  starter: {
    key: "starter",
    name: "Starter",
    description: "For single studios just starting out",
    price: { monthly: 200, quarterly: 180, yearly: 160 },
    limits: {
      outlets: 1,
      staff: 0,
      listings: null,
      contacts_storage: 1000,
      mac_base: 300,
      mac_ceiling: 500,
      mac_overage_pack_size: 100,
      mac_overage_pack_price_usd: 90,
      broadcast_rate_per_message: null,
    },
    features: {
      ai_auto_reply: true,
      ai_campaigns: true,
      synopsis_generation: false,
      analytics_dashboard: true,
      role_based_access: false,
      unlimited_contact_storage: false,
      automations: true,
      scheduled_broadcasts: false,
      custom_workflows: false,
      custom_connections: false,
      ask_dibs_ai: false,
    },
    stripe: {
      monthly: { priceId: "price_1UTwdyFlAk9QGxZMdAncN4Sc", productId: "prod_UmLn2WlAgh4gmL" },
      quarterly: { priceId: "price_1UTwLyFlAk9QGxZMaYAam0CH", productId: "prod_UmLn2WlAgh4gmL" },
      yearly: { priceId: "price_1UTwdyFlAk9QGxZMvOqIocPX", productId: "prod_UmLn2WlAgh4gmL" },
    },
  },
  growing: {
    key: "growing",
    name: "Growth",
    description: "For fast-scaling teams with 1–2 branches",
    price: { monthly: 450, quarterly: 400, yearly: 360 },
    limits: {
      outlets: 2,
      staff: 5,
      listings: null,
      contacts_storage: 2500,
      mac_base: 1000,
      mac_ceiling: 1200,
      mac_overage_pack_size: 100,
      mac_overage_pack_price_usd: 70,
      broadcast_rate_per_message: null,
    },
    features: {
      ai_auto_reply: true,
      ai_campaigns: true,
      synopsis_generation: true,
      analytics_dashboard: true,
      role_based_access: false,
      unlimited_contact_storage: false,
      automations: true,
      scheduled_broadcasts: false,
      custom_workflows: true,
      custom_connections: true,
      ask_dibs_ai: true,
    },
    stripe: {
      monthly: { priceId: "price_1UTwRDFlAk9QGxZMT2GQSgDE", productId: "prod_UmLoLXaxpEi2m9" },
      quarterly: { priceId: "price_1UTwRDFlAk9QGxZMuWaqGkAa", productId: "prod_UmLoLXaxpEi2m9" },
      yearly: { priceId: "price_1UTwRDFlAk9QGxZM9X1yFFse", productId: "prod_UmLoLXaxpEi2m9" },
    },
  },
  premium: {
    key: "premium",
    name: "Premium",
    description: "For established businesses with multiple branches",
    price: { monthly: 600, quarterly: 540, yearly: 480 },
    limits: {
      outlets: 5,
      staff: 15,
      listings: null,
      contacts_storage: 15000,
      mac_base: 1500,
      mac_ceiling: 12000,
      mac_overage_pack_size: 100,
      mac_overage_pack_price_usd: 60,
      broadcast_rate_per_message: 0.10,
    },
    features: {
      ai_auto_reply: true,
      ai_campaigns: true,
      synopsis_generation: true,
      analytics_dashboard: true,
      role_based_access: true,
      unlimited_contact_storage: false,
      automations: true,
      scheduled_broadcasts: true,
      custom_workflows: true,
      custom_connections: true,
      ask_dibs_ai: true,
    },
    stripe: {
      monthly: { priceId: "price_1Tmn8SFlAk9QGxZM99PEEyDC", productId: "prod_UmLqmdamQVEYQo" },
      quarterly: { priceId: "price_1UTwRtFlAk9QGxZMlWoLePQM", productId: "prod_UmLqmdamQVEYQo" },
      yearly: { priceId: "price_1Tmn8yFlAk9QGxZMPgwpbzlT", productId: "prod_UmLqmdamQVEYQo" },
    },
  },
  enterprise: {
    key: "enterprise",
    name: "Enterprise",
    description: "For large organisations needing custom scale, security, and dedicated support",
    price: { monthly: 299, yearly: 239 },
    limits: {
      outlets: null,
      staff: null,
      listings: null,
      contacts_storage: null,
      mac_base: null,
      mac_ceiling: null,
      mac_overage_pack_size: null,
      mac_overage_pack_price_usd: null,
      broadcast_rate_per_message: 0.10,
    },
    features: {
      ai_auto_reply: true,
      ai_campaigns: true,
      synopsis_generation: true,
      analytics_dashboard: true,
      role_based_access: true,
      unlimited_contact_storage: true,
      automations: true,
      scheduled_broadcasts: true,
      custom_workflows: true,
      custom_connections: true,
      ask_dibs_ai: true,
    },
    stripe: {
      monthly: { priceId: "price_1TEM17Fx39VnBqRbylvQTS3g", productId: "prod_U9domFST45yuNr" },
      yearly: { priceId: "price_1TEMGLFx39VnBqRbm0TOVUNw", productId: "prod_U9dp0f5gBkQ3f8" },
    },
  },
  // Internal-only comped plan. Selecting it in the onboarding wizard reveals
  // checkbox grants (free Growing and/or free Bookings) and provisions the
  // business at $0 with no Stripe subscription. Mirrors Growing limits for the
  // common case; the real stored tier is set per-grant by the checkout fn.
  early_supporter: {
    key: "early_supporter",
    name: "Early Supporter Plan",
    description: "Free perks for our earliest supporters — choose what to unlock below",
    price: { monthly: 0, yearly: 0 },
    limits: {
      outlets: 2,
      staff: null,
      listings: null,
      contacts_storage: null,
      mac_base: 500,
      mac_ceiling: 2500,
      mac_overage_pack_size: 100,
      mac_overage_pack_price_usd: 75,
      broadcast_rate_per_message: null,
    },
    features: {
      ai_auto_reply: true,
      ai_campaigns: true,
      synopsis_generation: true,
      analytics_dashboard: true,
      role_based_access: false,
      unlimited_contact_storage: true,
      automations: true,
      scheduled_broadcasts: false,
      custom_workflows: true,
      custom_connections: true,
      ask_dibs_ai: true,
    },
    stripe: null,
  },
};

const TEST_ADDONS: Record<string, AddOnConfig> = {
  bookings: {
    key: "bookings",
    name: "Dibs Bookings",
    description: "Powerful in-house booking system — designed to integrate seamlessly with your Dibs AI",
    price: { monthly: 20 },
    limits: { bookings: 200 },
    // Dibs Bookings add-on (S$20/mo, S$60/quarter). TEST.
    stripe: BOOKINGS_STRIPE_CATALOG.test,
  },
};

// Live plans
const LIVE_PLANS: Record<string, PlanConfig> = {
  // $1/mo Builder. Sign up and explore before launch. Import contacts and
  // generate hyper-personalised customer profiles, but NO automations run until
  // the business upgrades to a real plan. Self-serve on the pricing page.
  builder: {
    key: "builder",
    name: "Sandbox",
    description: "Explore Dibs before you launch — import contacts and build hyper-personalised customer profiles. Upgrade to turn on automations.",
    price: { monthly: 1, quarterly: 1, yearly: 1 },
    limits: {
      outlets: 1,
      staff: 1,
      listings: null,
      contacts_storage: null,
      mac_base: 50,
      mac_ceiling: 50,
      mac_overage_pack_size: null,
      mac_overage_pack_price_usd: null,
      broadcast_rate_per_message: null,
    },
    features: {
      ai_auto_reply: false,
      ai_campaigns: false,
      synopsis_generation: false,
      analytics_dashboard: false,
      role_based_access: false,
      unlimited_contact_storage: true,
      scheduled_broadcasts: false,
      automations: false,
      custom_workflows: false,
      custom_connections: false,
      ask_dibs_ai: false,
    },
    stripe: {
      // Sandbox ($1/mo, internal key "builder"). LIVE. Monthly-only; quarterly and yearly reuse the monthly price ID.
      monthly: { priceId: "price_1TmmFDFlAk9QGxZMBK84emam", productId: "prod_UmKvISG0cN5Dh5" },
      quarterly: { priceId: "price_1TmmFDFlAk9QGxZMBK84emam", productId: "prod_UmKvISG0cN5Dh5" },
      yearly: { priceId: "price_1TmmFDFlAk9QGxZMBK84emam", productId: "prod_UmKvISG0cN5Dh5" },
    },
  },
  // Minimal floor for cancelled / no-subscription / add-on-only businesses.
  // NOT a sellable tier, not rendered on the pricing page.
  free: {
    key: "free",
    name: "Free",
    description: "Minimal floor for accounts without an active paid plan",
    price: { monthly: 0, yearly: 0 },
    limits: {
      outlets: 1,
      staff: 1,
      listings: null,
      contacts_storage: null,
      mac_base: 50,
      mac_ceiling: 50,
      mac_overage_pack_size: null,
      mac_overage_pack_price_usd: null,
      broadcast_rate_per_message: null,
    },
    features: {
      ai_auto_reply: true,
      ai_campaigns: false,
      synopsis_generation: false,
      analytics_dashboard: false,
      role_based_access: false,
      unlimited_contact_storage: true,
      automations: true,
      scheduled_broadcasts: false,
      custom_workflows: false,
      custom_connections: false,
      ask_dibs_ai: false,
    },
    stripe: null,
  },
  // Starter: a single studio just starting out. One branch, owner only (no
  // staff seats). Replaced Lite (S$40) on 2026-10-24; the key stayed "starter",
  // so Lite subscribers resolve here. Unlike Growth: templates as-is only (no
  // custom workflows), no custom MCP/API, no Ask Dibs AI, and no AI-inferred
  // profile attributes (given + calculated facts only).
  starter: {
    key: "starter",
    name: "Starter",
    description: "For single studios just starting out",
    price: { monthly: 200, quarterly: 180, yearly: 160 },
    limits: {
      outlets: 1,
      staff: 0,
      listings: null,
      contacts_storage: 1000,
      mac_base: 300,
      mac_ceiling: 500,
      mac_overage_pack_size: 100,
      mac_overage_pack_price_usd: 90,
      broadcast_rate_per_message: null,
    },
    features: {
      ai_auto_reply: true,
      ai_campaigns: true,
      synopsis_generation: false,
      analytics_dashboard: true,
      role_based_access: false,
      unlimited_contact_storage: false,
      automations: true,
      scheduled_broadcasts: false,
      custom_workflows: false,
      custom_connections: false,
      ask_dibs_ai: false,
    },
    stripe: {
      monthly: { priceId: "price_1UTw0eFlAk9QGxZMql4hzxqH", productId: "prod_UmLATENoSsTwTC" },
      quarterly: { priceId: "price_1UTw0eFlAk9QGxZMHgDAdy3H", productId: "prod_UmLATENoSsTwTC" },
      yearly: { priceId: "price_1UTw0eFlAk9QGxZMv9LhO7wG", productId: "prod_UmLATENoSsTwTC" },
    },
  },
  growing: {
    key: "growing",
    name: "Growth",
    description: "For fast-scaling teams with 1–2 branches",
    price: { monthly: 450, quarterly: 400, yearly: 360 },
    limits: {
      outlets: 2,
      staff: 5,
      listings: null,
      contacts_storage: 2500,
      mac_base: 1000,
      mac_ceiling: 1200,
      mac_overage_pack_size: 100,
      mac_overage_pack_price_usd: 70,
      broadcast_rate_per_message: null,
    },
    features: {
      ai_auto_reply: true,
      ai_campaigns: true,
      synopsis_generation: true,
      analytics_dashboard: true,
      role_based_access: false,
      unlimited_contact_storage: false,
      automations: true,
      scheduled_broadcasts: false,
      custom_workflows: true,
      custom_connections: true,
      ask_dibs_ai: true,
    },
    stripe: {
      monthly: { priceId: "price_1UTw1qFlAk9QGxZMlTfZTgDe", productId: "prod_UmLBtqZPBR2jLS" },
      quarterly: { priceId: "price_1UTw5PFlAk9QGxZMyxVDNQ4k", productId: "prod_UmLBtqZPBR2jLS" },
      yearly: { priceId: "price_1UTw1qFlAk9QGxZMBtF6bVEI", productId: "prod_UmLBtqZPBR2jLS" },
    },
  },
  premium: {
    key: "premium",
    name: "Premium",
    description: "For established businesses with multiple branches",
    price: { monthly: 600, quarterly: 540, yearly: 480 },
    limits: {
      outlets: 5,
      staff: 15,
      listings: null,
      contacts_storage: 15000,
      mac_base: 1500,
      mac_ceiling: 12000,
      mac_overage_pack_size: 100,
      mac_overage_pack_price_usd: 60,
      broadcast_rate_per_message: 0.10,
    },
    features: {
      ai_auto_reply: true,
      ai_campaigns: true,
      synopsis_generation: true,
      analytics_dashboard: true,
      role_based_access: true,
      unlimited_contact_storage: false,
      automations: true,
      scheduled_broadcasts: true,
      custom_workflows: true,
      custom_connections: true,
      ask_dibs_ai: true,
    },
    stripe: {
      monthly: { priceId: "price_1TmmZMFlAk9QGxZMCVbERIYQ", productId: "prod_UmLFKrgSxUfv9Q" },
      quarterly: { priceId: "price_1UTw2UFlAk9QGxZMMt7SGbT2", productId: "prod_UmLFKrgSxUfv9Q" },
      yearly: { priceId: "price_1TmmaBFlAk9QGxZM9ra0iMD4", productId: "prod_UmLFKrgSxUfv9Q" },
    },
  },
  enterprise: {
    key: "enterprise",
    name: "Enterprise",
    description: "For large organisations needing custom scale, security, and dedicated support",
    price: { monthly: 299, yearly: 239 },
    limits: {
      outlets: null,
      staff: null,
      listings: null,
      contacts_storage: null,
      mac_base: null,
      mac_ceiling: null,
      mac_overage_pack_size: null,
      mac_overage_pack_price_usd: null,
      broadcast_rate_per_message: 0.10,
    },
    features: {
      ai_auto_reply: true,
      ai_campaigns: true,
      synopsis_generation: true,
      analytics_dashboard: true,
      role_based_access: true,
      unlimited_contact_storage: true,
      automations: true,
      scheduled_broadcasts: true,
      custom_workflows: true,
      custom_connections: true,
      ask_dibs_ai: true,
    },
    stripe: {
      monthly: { priceId: "price_1TMOL0FlAk9QGxZMzp2tVG9B", productId: "prod_UL4TFA4b9tNQyR" },
      yearly: { priceId: "price_1TMOLOFlAk9QGxZMIZBwxYFV", productId: "prod_UL4TFA4b9tNQyR" },
    },
  },
  // Internal-only comped plan. Selecting it in the onboarding wizard reveals
  // checkbox grants (free Growing and/or free Bookings) and provisions the
  // business at $0 with no Stripe subscription. Mirrors Growing limits for the
  // common case; the real stored tier is set per-grant by the checkout fn.
  early_supporter: {
    key: "early_supporter",
    name: "Early Supporter Plan",
    description: "Free perks for our earliest supporters — choose what to unlock below",
    price: { monthly: 0, yearly: 0 },
    limits: {
      outlets: 2,
      staff: null,
      listings: null,
      contacts_storage: null,
      mac_base: 500,
      mac_ceiling: 2500,
      mac_overage_pack_size: 100,
      mac_overage_pack_price_usd: 75,
      broadcast_rate_per_message: null,
    },
    features: {
      ai_auto_reply: true,
      ai_campaigns: true,
      synopsis_generation: true,
      analytics_dashboard: true,
      role_based_access: false,
      unlimited_contact_storage: true,
      automations: true,
      scheduled_broadcasts: false,
      custom_workflows: true,
      custom_connections: true,
      ask_dibs_ai: true,
    },
    stripe: null,
  },
};

const LIVE_ADDONS: Record<string, AddOnConfig> = {
  bookings: {
    key: "bookings",
    name: "Dibs Bookings",
    description: "Powerful in-house booking system — designed to integrate seamlessly with your Dibs AI",
    price: { monthly: 20 },
    limits: { bookings: 200 },
    // Dibs Bookings add-on (S$20/mo, S$60/quarter). LIVE. Existing subscribers
    // on prior add-on prices still resolve via LEGACY_ADDON_PRICE_IDS and keep
    // their current Stripe subscription item.
    stripe: BOOKINGS_STRIPE_CATALOG.live,
  },
};

// Active config based on mode
export const plans: Record<string, PlanConfig> = isStripeTestMode ? TEST_PLANS : LIVE_PLANS;
export const addOns: Record<string, AddOnConfig> = isStripeTestMode ? TEST_ADDONS : LIVE_ADDONS;

// Resolvers

/** Stripe price for `plan` on `cycle`, or null when it isn't sold on that cycle. */
export function planPriceIdForCycle(plan: PlanConfig | undefined, cycle: BillingCycle): string | null {
  return plan?.stripe?.[cycle]?.priceId ?? null;
}

/** What one charge costs (S$540 for Starter quarterly), or null when not sold on that cycle. */
export function planChargeForCycle(plan: PlanConfig | undefined, cycle: BillingCycle): number | null {
  const perMonth = plan?.price[cycle];
  return perMonth == null ? null : perMonth * CYCLE_MONTHS[cycle];
}

/** Stripe price for an add-on on a plan billed on `cycle`. Null = can't be added (yearly). */
export function addOnPriceIdForCycle(addOn: AddOnConfig | undefined, cycle: BillingCycle): string | null {
  if (cycle === "yearly") return null;
  return addOn?.stripe[cycle]?.priceId ?? null;
}

/**
 * Look up the CRM plan key for a given Stripe price ID. Returns null if
 * the price ID is not a CRM plan (might be an add-on, or unknown).
 */
export function getPlanKeyFromPriceId(priceId: string): string | null {
  for (const [key, plan] of Object.entries(plans)) {
    if (BILLING_CYCLES.some((cycle) => plan.stripe?.[cycle]?.priceId === priceId)) return key;
  }
  if (priceId in LEGACY_PRICE_TO_PLAN) {
    return LEGACY_PRICE_TO_PLAN[priceId];
  }
  return null;
}

/**
 * Look up the CRM plan key for a given Stripe product ID. Returns null if
 * the product is not a CRM plan.
 */
export function getPlanKeyFromProductId(productId: string): string | null {
  for (const [key, plan] of Object.entries(plans)) {
    if (BILLING_CYCLES.some((cycle) => plan.stripe?.[cycle]?.productId === productId)) return key;
  }
  return null;
}

/**
 * Look up the add-on key for a given Stripe price ID. Returns null if
 * the price ID is not an add-on.
 */
export function getAddOnKeyFromPriceId(priceId: string): string | null {
  for (const [key, addOn] of Object.entries(addOns)) {
    if (addOn.stripe.monthly.priceId === priceId || addOn.stripe.quarterly?.priceId === priceId) return key;
  }
  if (priceId in LEGACY_ADDON_PRICE_IDS) {
    return LEGACY_ADDON_PRICE_IDS[priceId];
  }
  return null;
}

/**
 * Resolve a full subscription from the price IDs on its line items.
 *
 * A subscription with no recognised CRM plan price defaults to "free". This
 * supports the migration case where the existing $19/mo customer has only a
 * Bookings add-on line item (no separate CRM line item).
 */
export function resolveSubscriptionFromPriceIds(priceIds: string[]): ResolvedSubscription {
  let planKey = "free";
  const addOnKeys: string[] = [];

  for (const priceId of priceIds) {
    const addOnKey = getAddOnKeyFromPriceId(priceId);
    if (addOnKey) {
      addOnKeys.push(addOnKey);
      continue;
    }
    const planKeyFromPrice = getPlanKeyFromPriceId(priceId);
    if (planKeyFromPrice) planKey = planKeyFromPrice;
  }

  return { planKey, addOnKeys };
}

export { LEGACY_PRICE_TO_PLAN };
